import { useState, useEffect, useCallback } from "react";
import { leaveEncashmentService } from "../services/leaveEncashmentService";
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useLeaveEncashment = ({ user, currentYear, showGeneralMessage, apiFilterPayload }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      if (!currentYear) return;
      // Pass both filters and currentYear to the service
      const payload = {
        apiFilterPayload,
        currentYear
      }
      const res = await leaveEncashmentService.getLeaveBalances(payload);
      const employees = res.data.employees || [];
      setData(employees);
      setOriginalData(employees);
    } catch (err) {
      console.error("Fetch Error:", err);
      showGeneralMessage("Failed to retrieve balances", false);
    } finally {
      setLoading(false);
    }
  }, [apiFilterPayload, currentYear, showGeneralMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPreviewData = async (customFilters, payrollPeriodId) => {
    try {
      const payload = {
        apiFilterPayload: customFilters,
        currentYear: currentYear,
        currentPayrollPeriodId: payrollPeriodId, // get the employee for the current selected period
        isPostedForCurrentPayroll: true // show only the employee that has posted time entries
      }
      const res = await leaveEncashmentService.getLeaveBalances(payload);
      return res.data.employees || [];
    } catch (err) {
      showGeneralMessage("Failed to fetch classification preview.", false);
      return [];
    }
  };

  const handleSearch = (value) => {
    if (!value || value.trim() === "") {
      setData(originalData);
      return;
    }
    const lower = value.toLowerCase();
    const filtered = originalData.filter(emp =>
      emp.fullName?.toLowerCase().includes(lower) ||
      emp.classificationDescription?.toLowerCase().includes(lower) ||
      emp.employeeId?.toString().toLowerCase().includes(lower)
    );
    setData(filtered);
  };

  const postToPayroll = async (employeeIds, payrollPeriodId, classificationId, targetProcess = "payroll") => {
    try {
      const payload = {
        employeeIds,
        payrollPeriodId,
        year: currentYear,
        classificationId
      }
      if (targetProcess === "finalpay") {
        await leaveEncashmentService.syncToFinalPay(employeeIds, payrollPeriodId, currentYear);
      } else {
        // await leaveEncashmentService.syncToPayroll(employeeIds, payrollPeriodId, currentYear);
        await leaveEncashmentService.syncToPayroll(payload);
      }

      const targetProcessCapitalize = targetProcess.charAt(0).toUpperCase() + targetProcess.slice(1);

      await SetAuditTrail({
        setupArea: targetProcessCapitalize,
        actionTaken: "Post Leave Encashment",
        field: "Post " + targetProcessCapitalize,
        newValue: `-`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id,
      });

      return true;
    } catch (err) {
      throw err;
    }
  };

  const revertLeaveEncashment = async (payrollPeriodId, classificationId, targetProcess = "payroll") => {
    try {
      if (targetProcess === "finalpay") {
        await leaveEncashmentService.revert(payrollPeriodId, classificationId, true); // true if revert for final pay
      } else {
        await leaveEncashmentService.revert(payrollPeriodId, classificationId, false); // false if revert for payroll
      }

      const targetProcessCapitalize = targetProcess.charAt(0).toUpperCase() + targetProcess.slice(1);

      await SetAuditTrail({
        setupArea: targetProcessCapitalize,
        actionTaken: "Revert Leave Encashment",
        field: "Revert " + targetProcessCapitalize,
        newValue: `-`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id,
      });

    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, data, setData,
    handleSearch,
    refresh: fetchData,
    getPreviewData,
    postToPayroll,
    revertLeaveEncashment,
    setLoading
  };
};